import React, { useState, useRef } from "react";
import { Form as AntForm, Button, Input, Select, Collapse } from "antd";
import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import type { FormInstance } from "antd/es/form";
import { AppButton, Form, FORM_FIELD_TYPES, IFormItems } from "site/ui/base";
import { useNavigate } from "react-router-dom";
import { routeNames } from "utils";
import { courseCategoryLabelMap } from "utils/types";

const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 40px;
  gap: 24px;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const NavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  color: #5575f7;
  font-size: 14px;
  padding: 0;
  margin-right: 8px;
  border: none;
  font-family: "Manrope", sans-serif;
  box-shadow: none;

  &:hover {
    color: #4563e6;
    background: none;
  }
`;

const Title = styled.h1`
  font-family: Manrope-Medium;
  font-size: 24px;
  line-height: 32px;
  color: #333;
  margin: 0;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
  }
`;

const Tab = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const TabCircle = styled.div<{ active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => (props.active ? "#5575f7" : "#e0e0e0")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  margin-bottom: 8px;
`;

const TabLabel = styled.span<{ active: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.active ? "#333" : "#999")};
  font-weight: ${(props) => (props.active ? "500" : "normal")};
`;

const FormContainer = styled.div`
  margin-top: 20px;
`;

const ModuleCollapse = styled(Collapse)`
  margin-bottom: 16px;

  .ant-collapse-header {
    padding: 12px 16px !important;
    font-size: 16px;
    font-weight: 500;
  }

  .ant-collapse-content-box {
    padding: 16px !important;
  }
`;

const LessonCollapse = styled(Collapse)`
  margin-top: 16px;

  .ant-collapse-header {
    padding: 10px 16px !important;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-collapse-content-box {
    padding: 16px !important;
  }
`;

const AddButton = styled(Button)`
  margin-top: 16px;
  color: #5575f7;
  border: 1px solid #5575f7;
  padding: 14px;
  border-radius: 15px;
  box-shadow: none;
  font-family: "Manrope", sans-serif;

  &:hover {
    color: #4563e6;
    background: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
`;

const uploadFileProps = {
  name: "file",
  multiple: false,
  action: "",
  onChange(info: any) {
    const { status } = info.file;
    if (status !== "uploading") {
      console.log(info.file, info.fileList);
    }
    if (status === "done") {
      console.log(`${info.file.name} file uploaded successfully.`);
    } else if (status === "error") {
      console.error(`${info.file.name} file upload failed.`);
    }
  },
  onDrop(e: React.DragEvent<HTMLDivElement>) {
    console.log("Dropped files", e.dataTransfer.files);
  },
};

interface Module {
  key: string;
  title: string;
  description: string;
  lessons: Lesson[];
  activeKey: string[];
}

interface Lesson {
  key: string;
  title: string;
  content: string;
}

export const EditCourse: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [modules, setModules] = useState<Module[]>([
    {
      key: "1",
      title: "",
      description: "",
      lessons: [{ key: "1", title: "", content: "" }],
      activeKey: ["1"],
    },
  ]);

  const handleBack = (): void => {
    navigate(routeNames.adminCourses);
  };

  const [activeModuleKeys, setActiveModuleKeys] = useState<string[]>(["1"]);
  const formRef = useRef<FormInstance>(null);
  const contentFormRef = useRef<FormInstance>(null);

  const getBasicDetailsFormItems = (): IFormItems[] => {
    return [
      {
        fieldType: FORM_FIELD_TYPES.UPLOAD,
        itemProps: {
          name: "courseImage",
          label: "Course Image",
        },
        fieldProps: {
          dragProps: {
            ...uploadFileProps,
            accept: "image/*",
          },
          text: "Drag & drop course image, or click to browse files",
          hint: "Supported formats: JPEG, PNG, GIF, WEBP...",
        },
      },
      {
        fieldType: FORM_FIELD_TYPES.SELECT,
        itemProps: {
          name: "category",
          label: "Category",
          rules: [{ required: true, message: "Please select a category" }],
        },
        fieldProps: {
          placeholder: "Select category",
          children: [
            <Option key="medicalExams" value="medicalExams">
              {courseCategoryLabelMap("medicalExams")}
            </Option>,
            <Option key="license" value="license">
              {courseCategoryLabelMap("license")}
            </Option>,
            <Option key="others" value="others">
              {courseCategoryLabelMap("others")}
            </Option>,
          ],
        },
      },
      {
        fieldType: FORM_FIELD_TYPES.TEXT,
        itemProps: {
          name: "courseTitle",
          label: "Course title",
          rules: [{ required: true, message: "Please enter a course title" }],
        },
        fieldProps: {
          placeholder: "Enter course title",
        },
      },
      {
        fieldType: FORM_FIELD_TYPES.TEXT_AREA,
        itemProps: {
          name: "courseDescription",
          label: "Course description",
          rules: [
            { required: true, message: "Please enter a course description" },
          ],
        },
        fieldProps: {
          placeholder: "Enter course description",
          rows: 4,
        },
      },
      {
        fieldType: FORM_FIELD_TYPES.SELECT,
        itemProps: {
          name: "courseType",
          label: "Course type",
          rules: [{ required: true, message: "Please select a course type" }],
        },
        fieldProps: {
          placeholder: "Select course type",
          children: [
            <Option key="free" value="free">
              Free
            </Option>,
            <Option key="premium" value="premium">
              Premium
            </Option>,
          ],
        },
      },
    ];
  };

  const getAdditionalInfoFormItems = (): IFormItems[] => {
    return [
      {
        fieldType: FORM_FIELD_TYPES.TEXT_AREA,
        itemProps: {
          name: "learningObjectives",
          label: "Learning objectives",
        },
        fieldProps: {
          placeholder: "Enter learning objectives",
          rows: 4,
        },
      },
      {
        fieldType: FORM_FIELD_TYPES.TEXT_AREA,
        itemProps: {
          name: "targetAudience",
          label: "Target audience",
        },
        fieldProps: {
          placeholder: "Describe who this course is for",
          rows: 4,
        },
      },
      {
        fieldType: FORM_FIELD_TYPES.TEXT_AREA,
        itemProps: {
          name: "prerequisites",
          label: "Prerequisites",
        },
        fieldProps: {
          placeholder: "List any prerequisites for this course",
          rows: 4,
        },
      },
    ];
  };

  const handleAddModule = () => {
    const newModuleKey = String(modules.length + 1);
    const newModule: Module = {
      key: newModuleKey,
      title: "",
      description: "",
      lessons: [{ key: "1", title: "", content: "" }],
      activeKey: ["1"],
    };

    setModules([...modules, newModule]);
    setActiveModuleKeys([...activeModuleKeys, newModuleKey]);
  };

  const handleAddLesson = (moduleIndex: number) => {
    const updatedModules = [...modules];
    const moduleToUpdate = { ...updatedModules[moduleIndex] };
    const newLessonKey = String(moduleToUpdate.lessons.length + 1);

    moduleToUpdate.lessons = [
      ...moduleToUpdate.lessons,
      { key: newLessonKey, title: "", content: "" },
    ];
    moduleToUpdate.activeKey = [...moduleToUpdate.activeKey, newLessonKey];

    updatedModules[moduleIndex] = moduleToUpdate;
    setModules(updatedModules);
  };

  const onModuleCollapseChange = (keys: string | string[]) => {
    setActiveModuleKeys(Array.isArray(keys) ? keys : [keys]);
  };

  const onLessonCollapseChange = (
    moduleIndex: number,
    keys: string | string[]
  ) => {
    const updatedModules = [...modules];
    updatedModules[moduleIndex].activeKey = Array.isArray(keys) ? keys : [keys];
    setModules(updatedModules);
  };

  const CourseContentTab = () => (
    <FormContainer>
      <AntForm ref={contentFormRef} layout="vertical">
        <ModuleCollapse
          activeKey={activeModuleKeys}
          onChange={onModuleCollapseChange}
        >
          {modules.map((module, moduleIndex) => (
            <Panel header={`Module ${moduleIndex + 1}`} key={module.key}>
              <AntForm.Item
                name={["modules", moduleIndex, "title"]}
                label="Title"
                rules={[
                  { required: true, message: "Please enter a module title" },
                ]}
              >
                <Input placeholder="Enter module title" />
              </AntForm.Item>

              <AntForm.Item
                name={["modules", moduleIndex, "description"]}
                label="Description"
              >
                <TextArea placeholder="Enter module description" rows={4} />
              </AntForm.Item>

              <LessonCollapse
                activeKey={module.activeKey}
                onChange={(keys) => onLessonCollapseChange(moduleIndex, keys)}
              >
                {module.lessons.map((lesson, lessonIndex) => (
                  <Panel header={`Lesson ${lessonIndex + 1}`} key={lesson.key}>
                    <AntForm.Item
                      name={[
                        "modules",
                        moduleIndex,
                        "lessons",
                        lessonIndex,
                        "title",
                      ]}
                      label="Title"
                      rules={[
                        {
                          required: true,
                          message: "Please enter a lesson title",
                        },
                      ]}
                    >
                      <Input placeholder="Enter lesson title" />
                    </AntForm.Item>

                    <AntForm.Item
                      name={[
                        "modules",
                        moduleIndex,
                        "lessons",
                        lessonIndex,
                        "content",
                      ]}
                      label="Content"
                    >
                      <TextArea placeholder="Enter lesson content" rows={10} />
                    </AntForm.Item>
                  </Panel>
                ))}
              </LessonCollapse>

              <AddButton
                type="link"
                icon={<PlusOutlined />}
                onClick={() => handleAddLesson(moduleIndex)}
              >
                Add another lesson
              </AddButton>
            </Panel>
          ))}
        </ModuleCollapse>

        <AddButton
          type="link"
          icon={<PlusOutlined />}
          onClick={handleAddModule}
        >
          Add another module
        </AddButton>
      </AntForm>
    </FormContainer>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Form
            formRef={formRef}
            formProps={{
              layout: "vertical",
            }}
            items={getBasicDetailsFormItems()}
          />
        );
      case 1:
        return <CourseContentTab />;
      case 2:
        return (
          <Form
            formRef={formRef}
            formProps={{
              layout: "vertical",
            }}
            items={getAdditionalInfoFormItems()}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = () => {
    if (activeTab === 0 || activeTab === 2) {
      formRef.current
        ?.validateFields()
        .then((values) => {
          console.log("Form values:", values);
          if (activeTab < 2) {
            setActiveTab(activeTab + 1);
          } else {
            const basicDetails = formRef.current?.getFieldsValue();
            const courseContent = contentFormRef.current?.getFieldsValue();
            const additionalInfo = formRef.current?.getFieldsValue();

            console.log("Final form submission:", {
              ...basicDetails,
              ...courseContent,
              ...additionalInfo,
            });
          }
        })
        .catch((errorInfo) => {
          console.log("Validate Failed:", errorInfo);
        });
    } else if (activeTab === 1) {
      contentFormRef.current
        ?.validateFields()
        .then((values) => {
          console.log("Content form values:", values);
          setActiveTab(activeTab + 1);
        })
        .catch((errorInfo) => {
          console.log("Validate Failed:", errorInfo);
        });
    }
  };

  return (
    <Container>
      <NavHeader>
        <BackButton type="text" icon={<LeftOutlined />} onClick={handleBack}>
          Course management
        </BackButton>
        <AppButton variant="primary">Update Course</AppButton>
      </NavHeader>

      <Title>Edit course</Title>

      <TabsContainer>
        <Tab active={activeTab === 0} onClick={() => setActiveTab(0)}>
          <TabCircle active={activeTab === 0}>1</TabCircle>
          <TabLabel active={activeTab === 0}>Basic Details</TabLabel>
        </Tab>
        <Tab active={activeTab === 1} onClick={() => setActiveTab(1)}>
          <TabCircle active={activeTab === 1}>2</TabCircle>
          <TabLabel active={activeTab === 1}>Course content</TabLabel>
        </Tab>
        <Tab active={activeTab === 2} onClick={() => setActiveTab(2)}>
          <TabCircle active={activeTab === 2}>3</TabCircle>
          <TabLabel active={activeTab === 2}>Additional Information</TabLabel>
        </Tab>
      </TabsContainer>

      {renderTabContent()}

      <ButtonContainer>
        <AppButton variant="secondary" onClick={handleBack}>
          Cancel
        </AppButton>
        <AppButton variant="primary" onClick={handleSubmit}>
          {activeTab === 2 ? "Submit" : "Continue"}
        </AppButton>
      </ButtonContainer>
    </Container>
  );
};
